import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, OrderPageData, OrderPageTypes } from '../../api';
import { OrderPageStatus } from '../../api/models/OrderPageStatus';
import { getOrderPages } from '../../utils/AccountUtils';
import { useCalendarFilter } from './CalendarFilterContext';
import { useLanguage } from '../../common/GeneralUtils';
import { getActiveLocations } from '../../common/filter/locationGrouping';
import { useAuth } from '../../session/InternalAuthProvider';

type SummaryLabel = { workshops: string; staff: string };

export const useCalendarFilterSummary = (): SummaryLabel => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const {
    isFilterActive,
    selectedOrderPageIds,
    showEmployees,
    showShifts,
    showNotes,
    showUnavailabilities,
    showOnlyMine,
  } = useCalendarFilter();

  return useMemo(() => {
    if (!isFilterActive) return { workshops: t('calendar.filter.label'), staff: '' };

    const activeOrderPages = getOrderPages(account).filter(
      (op: OrderPageData) => op.status !== OrderPageStatus.ARCHIVED,
    );
    const selected = selectedOrderPageIds
      ? activeOrderPages.filter((op: OrderPageData) => selectedOrderPageIds.includes(op.id))
      : activeOrderPages;
    const allWorkshopsSelected = selectedOrderPageIds === null || selected.length === activeOrderPages.length;

    let workshopsLine: string;
    if (allWorkshopsSelected) {
      workshopsLine = t('calendar.filter.summary.all_workshops');
    } else {
      const parts: string[] = [];
      const activeLocations = getActiveLocations(account);
      if (activeLocations.length > 1) {
        const locTotal = activeLocations.length;
        const locCount = activeLocations.filter((loc) =>
          selected.some((op: OrderPageData) => op.location_id === loc.id),
        ).length;
        if (locCount) parts.push(t('calendar.filter.summary.locations', { count: locCount, total: locTotal }));
      }
      const allPriv = activeOrderPages.filter((op: OrderPageData) => op.order_page_type === OrderPageTypes.PRIVATE);
      const allEvents = activeOrderPages.filter((op: OrderPageData) => op.order_page_type === OrderPageTypes.EVENT);
      const allCourses = activeOrderPages.filter((op: OrderPageData) => op.order_page_type === OrderPageTypes.COURSE);
      const privCount = selected.filter((op: OrderPageData) => op.order_page_type === OrderPageTypes.PRIVATE).length;
      const eventCount = selected.filter((op: OrderPageData) => op.order_page_type === OrderPageTypes.EVENT).length;
      const courseCount = selected.filter((op: OrderPageData) => op.order_page_type === OrderPageTypes.COURSE).length;
      if (privCount) parts.push(t('calendar.filter.summary.privates', { count: privCount, total: allPriv.length }));
      if (eventCount) parts.push(t('calendar.filter.summary.events', { count: eventCount, total: allEvents.length }));
      if (courseCount)
        parts.push(
          t('calendar.filter.summary.courses', {
            count: courseCount,
            total: allCourses.length,
          }),
        );
      workshopsLine = parts.join(' · ') || t('calendar.filter.summary.all_workshops');
    }

    const staffParts: string[] = [];
    if (showOnlyMine) staffParts.push(t('calendar.filter.summary.only_mine'));
    if (!showEmployees) staffParts.push(t('calendar.filter.summary.no_employees'));
    if (!showShifts) staffParts.push(t('calendar.filter.summary.no_shifts'));
    if (!showNotes) staffParts.push(t('calendar.filter.summary.no_notes'));
    if (!showUnavailabilities) staffParts.push(t('calendar.filter.summary.no_unavailabilities'));

    return { workshops: workshopsLine, staff: staffParts.join(' · ') };
  }, [
    isFilterActive,
    selectedOrderPageIds,
    account,
    showEmployees,
    showShifts,
    showNotes,
    showUnavailabilities,
    showOnlyMine,
    language,
    t,
  ]);
};
